import { ComboBox, DropdownSkeleton } from "@carbon/react";
import { ComboBoxProps } from "@carbon/react/lib/components/ComboBox/ComboBox";
import { ServerError } from "@flaminem/utils";
import {
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";

export type SinglePickerFormFieldProps<
  TItem,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  loading?: boolean;
  error?: ServerError | null;
  disabled?: boolean;
  placeholder?: string;
} & Pick<
  ComboBoxProps<TItem>,
  "items" | "helperText" | "titleText" | "invalidText" | "itemToString" | "direction" | "autoAlign"
> &
  Pick<UseControllerProps<TFieldValues, TName>, "rules" | "defaultValue" | "name">;

function SinglePickerFormField<
  TItem,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  items,
  titleText,
  invalidText,
  loading,
  error,
  rules,
  defaultValue,
  helperText,
  itemToString,
  disabled,
  placeholder,
  direction,
  autoAlign,
}: SinglePickerFormFieldProps<TItem, TFieldValues, TName>) {
  const { control, formState, clearErrors } = useFormContext<TFieldValues, TName>();

  if (loading) return <DropdownSkeleton />;

  const filterItems = (
    ...args: Parameters<NonNullable<ComboBoxProps<TItem>["shouldFilterItem"]>>
  ) => {
    const [{ itemToString, inputValue, item }] = args;
    const itemStr = itemToString ? itemToString?.(item) : item?.toString() ?? "";
    return itemStr.toLowerCase().includes(inputValue?.toLowerCase() ?? "");
  };

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => {
        return (
          <ComboBox
            autoAlign={autoAlign}
            id={name.toString()}
            itemToString={itemToString}
            items={items}
            titleText={titleText}
            invalid={disabled ? false : !!formState.errors?.[name] || !!error}
            invalidText={error?.message || invalidText}
            helperText={helperText}
            shouldFilterItem={filterItems}
            disabled={disabled}
            direction={direction}
            placeholder={placeholder}
            selectedItem={field.value}
            onChange={(data) => {
              field.onChange(data.selectedItem);
              clearErrors(name);
            }}
          />
        );
      }}
    />
  );
}

export default SinglePickerFormField;
